import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { login } from "../../services/api";
import { Eye, EyeOff, Mail, ChevronRight, X, CheckCircle, Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { NovalaLogo } from '../../components/NovalaLogo';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useSavedAccounts, saveAccount, removeAccount } from './hooks/useSavedAccounts';
import { TrustCard } from './components/TrustCard';
import { BackLink } from './components/BackLink';
import { initialsOf, timeAgo, maskEmail } from './utils';
import type { Screen, SavedAccount, LoginProps } from './types';

const API = "https://api.getnovala.com/api/v1";

export default function Login({ onLogin }: LoginProps): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { accounts: savedAccounts, setAccounts: setSavedAccounts } = useSavedAccounts();
  const [screen, setScreen] = useState<Screen>(savedAccounts.length > 0 ? "accountList" : "email");
  const [email, setEmail] = useState<string>(savedAccounts[0]?.email || "");
  const [emailInput, setEmailInput] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [codeSent, setCodeSent] = useState<boolean>(false);
  const [code, setCode] = useState<string>("");
  const [codeSending, setCodeSending] = useState<boolean>(false);
  const [codeError, setCodeError] = useState<string>("");
  const [codeVerifying, setCodeVerifying] = useState<boolean>(false);

  const [forgotEmail, setForgotEmail] = useState<string>("");
  const [forgotError, setForgotError] = useState<string>("");
  const [forgotSent, setForgotSent] = useState<boolean>(false);
  const [forgotSending, setForgotSending] = useState<boolean>(false);

  const [managing, setManaging] = useState<boolean>(false);

  const handlePasswordLogin = async (): Promise<void> => {
    if (!password) { setError(t("login.errPasswordRequired")); return; }
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      const token = res.data.access_token as string;
      const refreshToken = res.data.refresh_token as string | undefined;
      localStorage.setItem("token", token);
      localStorage.setItem("access_token", token);
      if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
      localStorage.setItem("user_email", email);
      saveAccount(email);
      try {
        const profRes = await fetch(API + "/auth/profile", { headers: { Authorization: "Bearer " + token } });
        if (profRes.ok) {
          const prof = await profRes.json();
          if (prof.full_name) {
            localStorage.setItem("full_name", prof.full_name);
            localStorage.setItem("user_name", prof.full_name);
            const firstWord = String(prof.full_name).trim().split(" ")[0];
            if (firstWord) localStorage.setItem("first_name", firstWord);
          }
          if (prof.first_name) localStorage.setItem("first_name", prof.first_name);
        }
      } catch (profErr) { /* non-fatal */ }
      onLogin(token, email);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        const d = detail.toLowerCase();
        if (d.includes("password")) setError(t("login.errIncorrectPassword"));
        else if (d.includes("email") || d.includes("not found")) {
          setError(t("login.errNoAccount"));
          setTimeout(() => { window.location.href = "/onboarding"; }, 1200);
        } else if (d.includes("invalid")) setError(t("login.errInvalidLogin"));
        else setError(detail);
      } else {
        setError(t("login.errConnection"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmailCode = async (): Promise<void> => {
    setCodeSending(true);
    setCodeError("");
    try {
      const res = await fetch(API + "/auth/send-login-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json();
        setCodeError(data.detail || t("login.errCouldNotSend"));
        return;
      }
      setCodeSent(true);
    } catch {
      setCodeError(t("login.errConnection"));
    } finally {
      setCodeSending(false);
    }
  };

  const handleVerifyCode = async (): Promise<void> => {
    if (code.length !== 6) { setCodeError(t("login.errCodeSix")); return; }
    setCodeVerifying(true);
    setCodeError("");
    try {
      const res = await fetch(API + "/auth/verify-login-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (!res.ok) { setCodeError(data.detail || t("login.errInvalidCode")); return; }
      const token = data.access_token as string;
      const refreshToken = data.refresh_token as string | undefined;
      localStorage.setItem("token", token);
      localStorage.setItem("access_token", token);
      if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
      localStorage.setItem("user_email", email);
      saveAccount(email);
      onLogin(token, email);
    } catch {
      setCodeError(t("login.errCouldNotVerify"));
    } finally {
      setCodeVerifying(false);
    }
  };

  const handleEmailSubmit = (): void => {
    setError("");
    if (!emailInput.trim() || !emailInput.includes("@")) {
      setError(t("login.errInvalidEmail"));
      return;
    }
    setEmail(emailInput.trim());
    setScreen("password");
  };

  const handleForgotPassword = async (): Promise<void> => {
    if (!forgotEmail.trim() || !forgotEmail.includes("@")) {
      setForgotError(t("login.errInvalidEmail"));
      return;
    }
    setForgotError("");
    setForgotSending(true);
    try {
      await fetch(API + "/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotSent(true);
    } catch {
      setForgotError(t("login.errCouldNotReset"));
    } finally {
      setForgotSending(false);
    }
  };

  const selectAccount = (acctEmail: string): void => {
    setEmail(acctEmail);
    setError(""); setPassword(""); setCodeSent(false); setCode(""); setCodeError("");
    setScreen("password");
  };

  const handleRemoveAccount = (acctEmail: string): void => {
    const remaining = removeAccount(acctEmail);
    setSavedAccounts(remaining);
    if (remaining.length === 0) { setManaging(false); setScreen("email"); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page p-6 font-sans">
      <div className={"flex w-full items-start " + (isMobile ? "max-w-full flex-col gap-5" : "max-w-content-max flex-row gap-10")}>

        <div className={"w-full " + (isMobile ? "max-w-full" : "max-w-card") + " flex-[1.4]"}>
          <NovalaLogo size="lg" centered />
          <div className="rounded-xl border border-hairline bg-white shadow-card" style={{ padding: "52px 60px" }}>

            {screen === "accountList" && (
              <div>
                <div className="mb-1.5 text-hero font-medium text-ink">{t("login.welcomeBack")}</div>
                <div className="mb-6 text-body text-muted">{t("login.welcomeBackSubtitle")}</div>

                {savedAccounts.map((acct, idx) => (
                  <div key={acct.email} onClick={() => !managing && selectAccount(acct.email)} className={"mb-2 flex items-center gap-3 rounded-md px-3.5 py-3 transition-colors " + (idx === 0 ? "border border-brand bg-surface-cardActive" : "border-hairline border bg-white") + (managing ? " cursor-default" : " cursor-pointer hover:border-brand/50")}>
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-bg text-small font-medium text-brand">{initialsOf(acct.email)}</div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-small font-medium text-ink">{acct.email}</div>
                      <div className="text-tiny text-muted">{timeAgo(acct.lastSignedIn, t)}</div>
                    </div>
                    {managing ? (
                      <button onClick={(e) => { e.stopPropagation(); handleRemoveAccount(acct.email); }} className="flex items-center border-none bg-transparent p-1 text-danger" aria-label={t("login.removeAccount")}>
                        <X size={16} />
                      </button>
                    ) : (
                      <ChevronRight size={16} className="text-ink" />
                    )}
                  </div>
                ))}

                <div onClick={() => { setEmailInput(""); setScreen("email"); }} className="mb-3 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-stroke px-3.5 py-2.5">
                  <Plus size={14} className="text-brand" />
                  <span className="text-small font-medium text-brand">{t("login.addAnotherAccount")}</span>
                </div>

                <div className="mb-3 text-center text-small text-sub">
                  <span onClick={() => setManaging((m) => !m)} className="cursor-pointer underline">
                    {managing ? t("login.backToSignIn") : t("login.manageSavedAccounts")}
                  </span>
                </div>

                <div className="border-t border-hairline pt-4 text-center text-small text-sub">
                  {t("login.newToNovala")}{" "}
                  <span onClick={() => navigate("/onboarding")} className="cursor-pointer font-medium text-brand underline">{t("login.createAccount")}</span>
                </div>
              </div>
            )}

            {screen === "email" && (
              <div>
                {savedAccounts.length > 0 && <BackLink text={t("login.backToSignIn")} onClick={() => setScreen("accountList")} />}
                <div className="mb-1.5 text-hero font-medium text-ink">{t("login.signInTitle")}</div>
                <div className="mb-6 text-body text-muted">{t("login.signInSubtitle")}</div>
                <div className="mb-3.5">
                  <Label htmlFor="login-email">{t("login.emailLabel")}</Label>
                  <Input id="login-email" autoFocus type="email" name="email" autoComplete="email" placeholder={t("login.emailPlaceholder")} value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); if (error) setError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                  />
                </div>
                {error && <Alert variant="danger" className="mb-3.5"><AlertDescription>{error}</AlertDescription></Alert>}
                <Button onClick={handleEmailSubmit} fullWidth>
                  {t("login.continue")} <ChevronRight size={16} />
                </Button>
                <div className="mt-5 border-t border-hairline pt-4 text-center text-small text-sub">
                  {t("login.newToNovala")}{" "}
                  <span onClick={() => navigate("/onboarding")} className="cursor-pointer font-medium text-brand underline">{t("login.createAccount")}</span>
                </div>
              </div>
            )}

            {screen === "password" && (
              <div>
                <BackLink text={t("login.useDifferentAccount")} onClick={() => { setScreen(savedAccounts.length > 0 ? "accountList" : "email"); setPassword(""); setError(""); setCodeSent(false); setCode(""); setCodeError(""); }} />
                <div className="mb-1.5 text-hero font-medium text-ink">{t("login.passwordTitle")}</div>
                <div className="mb-5 text-small font-medium text-brand">{email}</div>

                {!codeSent ? (
                  <>
                    <div className="mb-2">
                      <Label htmlFor="login-password">{t("login.passwordLabel")}</Label>
                      <div className="relative">
                        <Input id="login-password" type={showPassword ? "text" : "password"} name="password" autoComplete="current-password" placeholder={t("login.passwordPlaceholder")} value={password}
                          onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                          onKeyDown={(e) => e.key === "Enter" && handlePasswordLogin()}
                          className="pr-11"
                          autoFocus
                        />
                        <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3.5 top-1/2 flex -translate-y-1/2 items-center border-none bg-transparent text-sub">
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <div className="mb-4 text-right">
                      <span onClick={() => { setScreen("forgot"); setForgotEmail(email); }} className="cursor-pointer text-small font-medium text-brand underline">
                        {t("login.forgotPassword")}
                      </span>
                    </div>

                    {error && <Alert variant="danger" className="mb-3.5"><AlertDescription>{error}</AlertDescription></Alert>}

                    <Button onClick={handlePasswordLogin} disabled={loading} fullWidth>
                      {loading ? t("login.signingIn") : t("login.continue")}
                    </Button>

                    <div className="my-5 flex items-center gap-3">
                      <div className="h-px flex-1 bg-hairline" />
                      <span className="text-tiny text-muted">{t("login.orDivider")}</span>
                      <div className="h-px flex-1 bg-hairline" />
                    </div>

                    <div onClick={handleSendEmailCode} className={"flex items-center gap-2.5 rounded-md border border-stroke bg-white px-3.5 py-3 " + (codeSending ? "cursor-not-allowed opacity-60" : "cursor-pointer")}>
                      <Mail size={16} className="text-ink" />
                      <div>
                        <div className="text-small font-medium text-ink">
                          {codeSending ? t("login.sendingCode") : t("login.emailMeCode")}
                        </div>
                        <div className="text-tiny text-muted">{t("login.emailCodeSubtitle")}</div>
                      </div>
                    </div>

                    {codeError && <Alert variant="danger" className="mt-2.5"><AlertDescription>{codeError}</AlertDescription></Alert>}
                  </>
                ) : (
                  <div>
                    <div className="mb-5 flex items-center gap-2.5 rounded-md border border-brand-borderLight bg-brand-bg px-3.5 py-3">
                      <Mail size={16} className="text-brand" />
                      <span className="text-small text-sub">{t("login.codeSentTo")} <strong className="text-brand">{maskEmail(email)}</strong></span>
                    </div>

                    <div className="mb-3.5">
                      <Label htmlFor="login-code">{t("login.enterSixDigitCode")}</Label>
                      <Input id="login-code" autoFocus type="text" placeholder="000000" value={code} maxLength={6}
                        onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); if (codeError) setCodeError(""); }}
                        onKeyDown={(e) => e.key === "Enter" && handleVerifyCode()}
                        className="text-center font-mono text-heading font-medium tracking-[0.3em]"
                      />
                    </div>

                    {codeError && <Alert variant="danger" className="mb-3.5"><AlertDescription>{codeError}</AlertDescription></Alert>}

                    <Button onClick={handleVerifyCode} disabled={codeVerifying || code.length !== 6} fullWidth>
                      <CheckCircle size={16} />
                      {codeVerifying ? t("login.verifying") : t("login.verifyAndSignIn")}
                    </Button>

                    <div className="mt-3.5 text-center text-small text-muted">
                      <span onClick={() => { setCodeSent(false); setCode(""); setCodeError(""); }} className="cursor-pointer text-brand underline">{t("login.usePasswordInstead")}</span>
                      <span className="mx-2 text-stroke">|</span>
                      <span onClick={handleSendEmailCode} className="cursor-pointer text-brand underline">{t("login.resendCode")}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {screen === "forgot" && (
              <div>
                <BackLink text={t("login.backToSignIn")} onClick={() => { setScreen("password"); setForgotError(""); setForgotSent(false); }} />
                <div className="mb-1.5 text-hero font-medium text-ink">{t("login.forgotTitle")}</div>
                <div className="mb-5 text-small text-muted">{t("login.forgotSubtitle")}</div>

                <div className="mb-3.5">
                  <Label htmlFor="forgot-email">{t("login.emailLabel")}</Label>
                  <Input id="forgot-email" autoFocus type="email" name="email" autoComplete="email" placeholder={t("login.emailPlaceholder")} value={forgotEmail}
                    onChange={(e) => { setForgotEmail(e.target.value); if (forgotError) setForgotError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleForgotPassword()}
                  />
                </div>

                {forgotError && <Alert variant="danger" className="mb-3.5"><AlertDescription>{forgotError}</AlertDescription></Alert>}

                {forgotSent && (
                  <div className="mb-3.5 flex items-center gap-2.5 rounded-md border border-brand-borderLight bg-brand-bg px-3.5 py-3">
                    <CheckCircle size={16} className="text-brand" />
                    <span className="text-small text-sub">{t("login.resetLinkSent", { email: forgotEmail })}</span>
                  </div>
                )}

                {!forgotSent && (
                  <Button onClick={handleForgotPassword} disabled={forgotSending} fullWidth>
                    <Mail size={16} />
                    {forgotSending ? t("login.sendingCode") : t("login.sendResetLink")}
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="mt-4 text-center text-tiny text-helper">
            {t("login.termsFooter")}{" "}
            <a href="/terms" className="font-medium text-brand underline">{t("login.termsLink")}</a>
            {" "}{t("login.and")}{" "}
            <a href="/privacy" className="font-medium text-brand underline">{t("login.privacyLink")}</a>.
          </div>
        </div>

        <div className={"w-full flex-1 " + (isMobile ? "max-w-full mt-0" : "max-w-trust-panel mt-6")}>
          <div className="mb-3 text-small font-medium text-sub">{t("login.whyNovala")}</div>
          <TrustCard title={t("login.trustCard1Title")} desc={t("login.trustCard1Desc")} />
          <TrustCard title={t("login.trustCard2Title")} desc={t("login.trustCard2Desc")} />
          <TrustCard title={t("login.trustCard3Title")} desc={t("login.trustCard3Desc")} />
          <TrustCard title={t("login.trustCard4Title")} desc={t("login.trustCard4Desc")} />
        </div>

      </div>
    </div>
  );
}
